import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { lots } from "@/api/lots/useLotsQuery";
import { stockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  DefaultApiWmsBulkOutgoingPostRequest,
  PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner,
  WmsBulkOutgoingPostRequestDataInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface PurchaseBatchOutgoingLogicProps {
  purchaseOrder?: () => (
    | PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner
    | undefined
  )[];
  purchaseOrderItems?: AuthSignupPost201ResponseEquipmentPurchaseOrderItem[] | undefined;
}

export const usePurchaseBatchOutgoingLogic = (params: PurchaseBatchOutgoingLogicProps) => {
  const { purchaseOrder, purchaseOrderItems } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { closeModal } = useModal();

  const [lotId, setLotId] = useState<{
    lotId: string | null;
    index: number | null;
  }>({ lotId: null, index: null });
  const selectedItems =
    purchaseOrder === undefined
      ? purchaseOrderItems
      : purchaseOrder().filter(
          (item): item is PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner =>
            item !== undefined,
        );

  const { mutateAsync: outgoingMutate } = useMutation(
    (params: DefaultApiWmsBulkOutgoingPostRequest) =>
      mutateInventories
        .bulkOutgoing(params)
        .mutationFn(params as DefaultApiWmsBulkOutgoingPostRequest | any),
    {
      onSuccess: () => {
        customAlert(t("delivery.successMessage"), t("delivery.success"), "green");
        queryClient.invalidateQueries(["inventories"]);
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        queryClient.invalidateQueries(["purchaseOrders"]);
        closeModal(null);
      },
      onError: () => {
        customAlert(t("delivery.failMessage"), t("delivery.fail"), "red");
      },
    },
  );
  const form = useForm<{ items: WmsBulkOutgoingPostRequestDataInner[] }>({
    initialValues: {
      items: (selectedItems ?? [])?.map((item) => ({
        ...item,
        itemCode: item.itemCode ?? "",
        purchaseOrderItemId: item.id,
        locationCode: item.sourceLocationCode ?? "",
        quantity: item.quantity ?? "",
        inputQuantity: Number(item.quantity) - Number(item.sumOfDeliveredQuantity),
        lotId: 0,
        lotInfo: {
          name: null,
          expiration: null,
        },
        stockPlanId: null,
        transportationId:
          "transportationId" in item ? ((item as any).transportationId ?? null) : null,
      })),
    },
  });

  const stockPlanQueries = useQueries({
    queries: form.values.items.map((item, index) => {
      const { queryKey, queryFn } = stockPlans.get({
        query: {
          id: item.stockPlanId,
        },
        populate: ["transportation", "lot"],
      });
      return {
        queryKey,
        queryFn,
        enabled: !!item.stockPlanId,
        staleTime: 0,
      };
    }),
  });

  const sourceLocationCode = stockPlanQueries
    .map((query) => query.data?.data?.rows?.[0]?.sourceLocationCode ?? "")
    .filter((code) => code !== "");

  const stockPlanLot = stockPlanQueries
    .map((query) => query.data?.data?.rows?.[0]?.lotData)
    .filter((lot) => lot !== null && lot !== undefined);

  useEffect(() => {
    stockPlanQueries.forEach((query, index) => {
      if (!query.isSuccess) return;

      const stockPlan = query.data?.data?.rows?.[0];
      if (!stockPlan) return;

      const lot = stockPlan.lotData;
      const locationCode = stockPlan.sourceLocationCode;

      if (lot) {
        form.setFieldValue(`items.${index}.lotId`, lot.id);
        form.setFieldValue(`items.${index}.lotInfo.name`, lot.name);
        form.setFieldValue(`items.${index}.lotInfo.expiration`, lot.expiration);
      }
      if (locationCode) {
        form.setFieldValue(`items.${index}.locationCode`, locationCode);
      }
    });
  }, [stockPlanQueries.map((q) => q.data)]); // 여기는 여전히 리렌더되지만, isSuccess 체크로 방지

  // 재고계획을 선택 안했을 경우
  const { data } = useQuery({
    ...lots.detail({
      lotId: Number(lotId.lotId) ?? "",
    }),
    enabled: !!lotId.lotId,
    onSuccess: (data) => {
      const name = data?.data?.name;
      const expiration = data?.data?.expiration;
      if (lotId.index !== null) {
        form.setFieldValue(`items.${lotId.index}.lotInfo.name`, name);
        form.setFieldValue(`items.${lotId.index}.lotInfo.expiration`, expiration);
      }

      setLotId({ lotId: null, index: null });
    },
  });

  const onSubmit = async () => {
    try {
      const payload =
        form.values?.items?.map((item) => ({
          itemCode: item.itemCode,
          locationCode: item.locationCode,
          quantity: "inputQuantity" in item ? (item as any).inputQuantity.toString() : "",
          lotId: Number(item.lotId) ?? Number(lotId.lotId),
          lotInfo: {
            name: item.lotInfo?.name,
            expiration: item.lotInfo?.expiration,
          },
          actionFrom: "WEB",
          purchaseOrderItemId: item.purchaseOrderItemId ?? null,
          transportationId: item.transportationId,
          stockPlanId: item.stockPlanId,
        })) ?? [];
      if (payload.length > 0) {
        outgoingMutate({
          wmsBulkOutgoingPostRequest: {
            data: payload,
          },
        });
      }
    } catch (e) {
      customAlert(t("delivery.failMessage"), t("delivery.fail"), "red");
    }
  };

  return {
    form,
    onSubmit,
    setLotId,
    selectedItems,
    stockPlanLot,
    sourceLocationCode,
  };
};
